// src/hooks/plan/useStaffManagePage.js
import { useState, useMemo, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import planApi from '@api/planApi';
import { normalizePage } from '@utils/paging';
import { AuthContext } from '@context';

export default function useStaffManagePage() {
  const queryClient = useQueryClient();
  const { auth } = useContext(AuthContext);
  
  const [filters, setFilters] = useState({ deptNm: '전체', useYn: '전체', searchKeyword: '', pageNum: 1, pageSize: 10 });
  const [modal, setModal] = useState({ mode: null, data: null });
  // 🌟 엑셀 업로드 모달 상태
  const [isExcelOpen, setIsExcelOpen] = useState(false);

  const query = useQuery({
    queryKey: ['plan', 'staffs', filters],
    queryFn: () => planApi.listStaffs(filters), 
    keepPreviousData: true,
  });

  const page = useMemo(() => normalizePage(query.data) || { rows: [], total: 0, pageNum: filters.pageNum, pageSize: filters.pageSize }, [query.data, filters]);

  const invalidateStaffs = () => {
    queryClient.invalidateQueries({ queryKey: ['plan', 'staffs'] });
    queryClient.invalidateQueries({ queryKey: ['plan', 'visits'] });
    // 방문 데이터가 바뀌면 산출물 관리 페이지의 데이터도 바뀌어야 하므로 같이 날려줍니다.
    queryClient.invalidateQueries({ queryKey: ['plan', 'deliverables'] }); 
    queryClient.invalidateQueries({ queryKey: ['plan', 'assignments'] });
    queryClient.invalidateQueries({ queryKey: ['plan', 'progress'] });
    queryClient.invalidateQueries({ queryKey: ['plan', 'dashboard'] });
  };

  const createMutation = useMutation({ mutationFn: (payload) => planApi.createStaff(payload), onSuccess: invalidateStaffs });
  const updateMutation = useMutation({ mutationFn: ({ staffId, payload }) => planApi.updateStaff(staffId, payload), onSuccess: invalidateStaffs });
  const deleteMutation = useMutation({ mutationFn: (staffId) => planApi.deleteStaff(staffId), onSuccess: invalidateStaffs });
  const toggleMutation = useMutation({
    mutationFn: ({ staffId, currentUseYn }) => planApi.updateStaff(staffId, { useYn: currentUseYn === 'Y' ? 'N' : 'Y' }),
    onSuccess: invalidateStaffs
  });

  // 🌟 엑셀 업로드 Mutation
  const excelUploadMutation = useMutation({
    mutationFn: (file) => planApi.uploadStaffExcel(file),
    onSuccess: () => {
      invalidateStaffs();
      setIsExcelOpen(false); // 성공 시 닫기
    }
  });

  // 🌟 템플릿 다운로드 핸들러
  const handleDownloadTemplate = async () => {
    try {
      const response = await planApi.downloadStaffExcelTemplate();
      // Blob 다운로드 처리
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '수행요원_등록양식.xlsx'); 
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      throw e; 
    }
  };

  const onSearch = () => setFilters(p => ({ ...p, pageNum: 1 }));

  return {
    filters, setFilters, query, page, onSearch,
    modal, setModal, isExcelOpen, setIsExcelOpen,
    createStaff: createMutation.mutateAsync, isCreating: createMutation.isPending,
    updateStaff: updateMutation.mutateAsync, isUpdating: updateMutation.isPending,
    deleteStaff: deleteMutation.mutateAsync,
    toggleUseYn: toggleMutation.mutateAsync,
    uploadExcel: excelUploadMutation.mutateAsync, isUploadingExcel: excelUploadMutation.isPending,
    downloadTemplate: handleDownloadTemplate,
    auth
  };
}